// client/src/components/TenderModal.jsx
import ContractReviewModal from './ContractReviewModal';
import BidNoBidSidebar from './BidNoBidSidebar';
import { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../api';
import { useUser } from '../context/UserContext';
import { toInputDate, STATUS_OPTIONS, STATUS_META, isConstructionType, requiresAddress, requiresW3W } from '../utils';
import { X, ChevronRight, ChevronLeft, HelpCircle, CheckCircle, AlertTriangle, MessageSquare } from 'lucide-react';

// ── Tooltip ───────────────────────────────────────────────────────────────────
const Tip = ({ text, show }) => {
  const [vis, setVis] = useState(false);
  if (!show) return null;
  return (
    <span style={{ position:'relative', display:'inline-flex', marginLeft:5, verticalAlign:'middle' }}>
      <HelpCircle size={14} color="#475569" style={{ cursor:'pointer' }}
        onMouseEnter={() => setVis(true)} onMouseLeave={() => setVis(false)} />
      {vis && (
        <span style={{ position:'absolute', bottom:'calc(100% + 6px)', left:'50%', transform:'translateX(-50%)',
          background:'var(--dropdown-bg)', border:'1px solid var(--border)', borderRadius:7,
          padding:'7px 10px', fontSize:11, color:'var(--text-dim)', width:220, zIndex:500,
          lineHeight:1.5, whiteSpace:'normal', pointerEvents:'none', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>
          {text}
        </span>
      )}
    </span>
  );
};

const Input = ({ label, tip, type='text', value, onChange, placeholder, required, disabled, showTips }) => (
  <div style={{ marginBottom:14 }}>
    <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>
      {label}{required && <span style={{color:'#ef4444'}}> *</span>}
      <Tip text={tip} show={showTips && !!tip} />
    </label>
    <input type={type} value={value||''} onChange={onChange} placeholder={placeholder}
      required={required} disabled={disabled}
      style={{ width:'100%', padding:'9px 12px',
        background: disabled ? 'var(--surface2)' : 'var(--input-bg)',
        border:'1px solid var(--border)', borderRadius:8,
        color: disabled ? 'var(--text-dim)' : 'var(--text)', fontSize:13, boxSizing:'border-box' }} />
  </div>
);

const Select = ({ label, tip, value, onChange, children, disabled, showTips }) => (
  <div style={{ marginBottom:14 }}>
    <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>
      {label}<Tip text={tip} show={showTips && !!tip} />
    </label>
    <select value={value||''} onChange={onChange} disabled={disabled}
      style={{ width:'100%', padding:'9px 12px',
        background: disabled ? 'var(--surface2)' : 'var(--input-bg)',
        border:'1px solid var(--border)', borderRadius:8,
        color: disabled ? 'var(--text-dim)' : 'var(--text)', fontSize:13, boxSizing:'border-box' }}>
      {children}
    </select>
  </div>
);

const Textarea = ({ label, tip, value, onChange, placeholder, rows=3, required, showTips }) => (
  <div style={{ marginBottom:14 }}>
    <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>
      {label}{required && <span style={{color:'#ef4444'}}> *</span>}
      <Tip text={tip} show={showTips && !!tip} />
    </label>
    <textarea value={value||''} onChange={onChange} placeholder={placeholder} rows={rows}
      style={{ width:'100%', padding:'9px 12px', background:'var(--surface2)',
        border:'1px solid var(--border)', borderRadius:8, color:'var(--text)',
        fontSize:13, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit' }} />
  </div>
);

// ── CompanyTypeahead ─────────────────────────────────────────────────────────
const CompanyTypeahead = ({ value, companyId, onChange, onClear, disabled, showTips, required }) => {
  const [query, setQuery]       = useState(value || '');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName]   = useState('');
  const [createErr, setCreateErr] = useState('');
  const debounce = useRef(null);
  const wrapRef  = useRef(null);

  useEffect(() => { if (value && value !== query) setQuery(value); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((q) => {
    clearTimeout(debounce.current);
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const data = await api.get(`/api/crm/companies?search=${encodeURIComponent(q)}`);
        setResults(data || []);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
  }, []);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (!v) { onClear(); setOpen(false); return; }
    if (companyId) onClear();
    search(v);
  };

  const select = (company) => {
    setQuery(company.name);
    setOpen(false);
    setCreating(false);
    onChange(company.id, company.name);
  };

  const handleCreate = async () => {
    if (!newName.trim()) { setCreateErr('Company name is required'); return; }
    setCreateErr('');
    try {
      const created = await api.post('/api/crm/companies', { name: newName.trim() });
      select(created);
      setCreating(false);
      setNewName('');
    } catch (e) { setCreateErr(e.message || 'Failed to create company'); }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px',
    background: disabled ? 'var(--surface2)' : 'var(--input-bg)',
    border: `1px solid ${companyId ? '#10b981' : 'var(--border)'}`,
    borderRadius: 8, color: disabled ? 'var(--text-dim)' : 'var(--text)',
    fontSize: 13, boxSizing: 'border-box',
  };

  return (
    <div style={{ marginBottom: 14 }} ref={wrapRef}>
      <label style={{ display: 'block', color: 'var(--text-dim)', fontSize: 12, marginBottom: 4 }}>
        Client / Company{required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          value={query}
          onChange={handleInput}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          disabled={disabled}
          placeholder="Search companies…"
          style={inputStyle}
          autoComplete="off"
        />
        {companyId && !loading && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontSize: 12 }}>✓</span>
        )}
        {loading && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: 11 }}>…</span>
        )}
        {open && (results.length > 0 || query.trim()) && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400,
            background: 'var(--card-bg)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 2, maxHeight: 200, overflowY: 'auto',
          }}>
            {results.map(c => (
              <div key={c.id} onMouseDown={() => select(c)}
                style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)',
                  borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--teal-dim)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {c.name}
              </div>
            ))}
            <div onMouseDown={() => { setCreating(true); setNewName(query); setOpen(false); }}
              style={{ padding: '9px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--teal)',
                fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              + Create &ldquo;{query}&rdquo;
            </div>
          </div>
        )}
      </div>
      {creating && (
        <div style={{ marginTop: 8, padding: '12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>New Company</p>
          <input value={newName} onChange={e => setNewName(e.target.value)}
            placeholder="Company name"
            style={{ width: '100%', padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', marginBottom: 6 }} />
          {createErr && <p style={{ color: '#f87171', fontSize: 11, marginBottom: 6 }}>{createErr}</p>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onMouseDown={handleCreate}
              style={{ flex: 1, padding: '7px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, color: '#10b981', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Save Company
            </button>
            <button onMouseDown={() => { setCreating(false); setCreateErr(''); }}
              style={{ padding: '7px 12px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-dim)', fontSize: 12, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── ContactTypeahead ──────────────────────────────────────────────────────────
const ContactTypeahead = ({ value, contactId, companyId, onChange, onClear, disabled }) => {
  const [query, setQuery]       = useState(value || '');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [creating, setCreating] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: '', last_name: '', email: '' });
  const [createErr, setCreateErr]   = useState('');
  const debounce = useRef(null);
  const wrapRef  = useRef(null);

  useEffect(() => { if (value && value !== query) setQuery(value); }, [value]);

  useEffect(() => {
    if (!companyId) { setQuery(''); setResults([]); onClear(); }
  }, [companyId]);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const search = useCallback((q) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (companyId) params.set('company_id', companyId);
        if (q.trim()) params.set('search', q);
        const data = await api.get(`/api/crm/contacts?${params}`);
        setResults(data || []);
        setOpen(true);
      } catch { setResults([]); }
      setLoading(false);
    }, 250);
  }, [companyId]);

  const handleInput = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (!v) onClear();
    if (contactId) onClear();
    search(v);
  };

  const handleFocus = () => { if (companyId) search(query); };

  const select = (contact) => {
    const name = `${contact.first_name} ${contact.last_name}`;
    setQuery(name);
    setOpen(false);
    setCreating(false);
    onChange(contact.id, name);
  };

  const handleCreate = async () => {
    if (!newContact.first_name.trim() || !newContact.last_name.trim()) {
      setCreateErr('First and last name are required'); return;
    }
    setCreateErr('');
    try {
      const created = await api.post('/api/crm/contacts', {
        first_name: newContact.first_name.trim(),
        last_name:  newContact.last_name.trim(),
        email:      newContact.email.trim() || null,
        company_id: companyId || null,
      });
      select(created);
      setCreating(false);
      setNewContact({ first_name: '', last_name: '', email: '' });
    } catch (e) { setCreateErr(e.message || 'Failed to create contact'); }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px',
    background: (!companyId || disabled) ? 'var(--surface2)' : 'var(--input-bg)',
    border: `1px solid ${contactId ? '#10b981' : 'var(--border)'}`,
    borderRadius: 8, color: (!companyId || disabled) ? 'var(--text-dim)' : 'var(--text)',
    fontSize: 13, boxSizing: 'border-box',
  };

  return (
    <div style={{ marginBottom: 14 }} ref={wrapRef}>
      <label style={{ display: 'block', color: 'var(--text-dim)', fontSize: 12, marginBottom: 4 }}>
        Contact
        {!companyId && <span style={{ color: 'var(--text-dim)', fontStyle: 'italic', marginLeft: 6, fontSize: 11 }}>— select company first</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          value={query}
          onChange={handleInput}
          onFocus={handleFocus}
          disabled={!companyId || disabled}
          placeholder={companyId ? 'Search contacts…' : 'Select company first'}
          style={inputStyle}
          autoComplete="off"
        />
        {contactId && !loading && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: '#10b981', fontSize: 12 }}>✓</span>
        )}
        {loading && (
          <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)', fontSize: 11 }}>…</span>
        )}
        {open && companyId && (results.length > 0 || query.trim()) && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400,
            background: 'var(--card-bg)', border: '1px solid var(--border)',
            borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.4)', marginTop: 2, maxHeight: 200, overflowY: 'auto',
          }}>
            {results.map(c => (
              <div key={c.id} onMouseDown={() => select(c)}
                style={{ padding: '9px 12px', fontSize: 13, cursor: 'pointer', color: 'var(--text)',
                  borderBottom: '1px solid var(--border)' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--teal-dim)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span>{c.first_name} {c.last_name}</span>
                {c.job_title && <span style={{ color: 'var(--text-dim)', fontSize: 11, marginLeft: 6 }}>{c.job_title}</span>}
                {c.email && <span style={{ color: 'var(--text-dim)', fontSize: 11, marginLeft: 6 }}>· {c.email}</span>}
              </div>
            ))}
            <div onMouseDown={() => {
                const parts = query.trim().split(' ');
                setNewContact({ first_name: parts[0] || '', last_name: parts.slice(1).join(' ') || '', email: '' });
                setCreating(true); setOpen(false);
              }}
              style={{ padding: '9px 12px', fontSize: 12, cursor: 'pointer', color: 'var(--teal)', fontWeight: 600 }}>
              + Add new contact
            </div>
          </div>
        )}
      </div>
      {creating && (
        <div style={{ marginTop: 8, padding: '12px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 8, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>New Contact</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
            <input value={newContact.first_name} onChange={e => setNewContact(n => ({ ...n, first_name: e.target.value }))}
              placeholder="First name *"
              style={{ padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' }} />
            <input value={newContact.last_name} onChange={e => setNewContact(n => ({ ...n, last_name: e.target.value }))}
              placeholder="Last name *"
              style={{ padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <input value={newContact.email} onChange={e => setNewContact(n => ({ ...n, email: e.target.value }))}
            placeholder="Email (optional)"
            style={{ width: '100%', padding: '8px 10px', background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)', fontSize: 13, boxSizing: 'border-box', marginBottom: 6 }} />
          {createErr && <p style={{ color: '#f87171', fontSize: 11, marginBottom: 6 }}>{createErr}</p>}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onMouseDown={handleCreate}
              style={{ flex: 1, padding: '7px', background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: 6, color: '#10b981', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              Save Contact
            </button>
            <button onMouseDown={() => { setCreating(false); setCreateErr(''); }}
              style={{ padding: '7px 12px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text-dim)', fontSize: 12, cursor: 'pointer' }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const STAGES = [
  { label:'Initial Entry',  desc:'Project details and assignment' },
  { label:'Estimate',       desc:'Prepare estimate and IDNO/AV if applicable' },
  { label:'Manager Review', desc:'Manager approval before submission' },
  { label:'Submission',     desc:'Submit to client' },
  { label:'Outcome',        desc:'Record the decision' },
];

const STAGE_TIPS = [
  'Enter core project details — client, deadlines, estimator, and work classification.',
  'Prepare the estimate value. If an IDNO offer has been received, record it here. Click "Estimate Ready — Send for Review" when complete to notify the manager.',
  'The manager reviews and either approves or rejects. Rejection requires a written reason and sends the tender back to the Estimate stage.',
  'Record the date the tender was formally submitted to the client.',
  'Record the final decision date and outcome. If Lost, client feedback must be recorded — this can be done later but is mandatory.',
];


// ── Additional Utilities (Water / Gas / Telecoms) ────────────────────────────────────
const UTIL_TYPES = ['New Connection', 'Alteration/Diversion', 'Disconnection'];

function UtilitySection({ label, prefix, form, setVal, toInputDate }) {
  const [open, setOpen] = useState(false);
  const hasData = !!(form[prefix + '_type'] || form[prefix + '_provider'] ||
                  form[prefix + '_application_date'] || form[prefix + '_received_date'] ||
                  form[prefix + '_notes']);
  useEffect(() => { if (hasData) setOpen(true); }, []);
  const inputStyle = { width:'100%', padding:'7px 10px', borderRadius:6,
    background:'var(--input-bg)', border:'1px solid var(--border)',
    color:'var(--text)', fontSize:13 };
  const labelStyle = { fontSize:11, color:'var(--text-muted)', display:'block', marginBottom:4 };
  return (
    <div style={{ borderTop:'1px solid rgba(255,255,255,0.07)', paddingTop:8, marginTop:4 }}>
      <button type='button' onClick={() => setOpen(o => !o)} style={{
        background:'none', border:'none', cursor:'pointer',
        display:'flex', alignItems:'center', gap:6,
        color: open ? '#93c5fd' : 'var(--text-muted)',
        fontSize:11, fontWeight:600, textTransform:'uppercase',
        letterSpacing:0.6, padding:'2px 0', width:'100%'
      }}>
        <span style={{ fontSize:10 }}>{open ? '▾' : '▸'}</span>
        {label}
        {hasData && !open && (
          <span style={{ marginLeft:6, fontSize:9, background:'rgba(59,130,246,0.15)',
            color:'#93c5fd', padding:'1px 6px', borderRadius:4 }}>data</span>
        )}
      </button>
      {open && (
        <div style={{ marginTop:10, display:'flex', flexDirection:'column', gap:10 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Type</label>
              <select value={form[prefix + '_type']} onChange={e => setVal(prefix + '_type', e.target.value)} style={inputStyle}>
                <option value=''>— Select —</option>
                {UTIL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Provider / Incumbent</label>
              <input type='text' value={form[prefix + '_provider']} onChange={e => setVal(prefix + '_provider', e.target.value)}
                placeholder={label === 'Water' ? 'e.g. Thames Water' : label === 'Gas' ? 'e.g. Cadent' : 'e.g. Openreach'}
                style={inputStyle} />
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div>
              <label style={labelStyle}>Application Date</label>
              <input type='date' value={toInputDate(form[prefix + '_application_date'])} onChange={e => setVal(prefix + '_application_date', e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Received Date</label>
              <input type='date' value={toInputDate(form[prefix + '_received_date'])} onChange={e => setVal(prefix + '_received_date', e.target.value)} style={inputStyle} />
            </div>
          </div>
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea value={form[prefix + '_notes']} onChange={e => setVal(prefix + '_notes', e.target.value)}
              rows={2} placeholder='Reference numbers, contacts, additional detail…'
              style={{ ...inputStyle, resize:'vertical' }} />
          </div>
        </div>
      )}
    </div>
  );
}

function AdditionalUtilities({ form, setVal, toInputDate }) {
  const hasAny = !!(form.water_type || form.water_provider || form.water_application_date ||
     form.gas_type || form.gas_provider || form.gas_application_date ||
     form.telecoms_type || form.telecoms_provider || form.telecoms_application_date);
  const [open, setOpen] = useState(hasAny);
  return (
    <div style={{ background:'rgba(59,130,246,0.04)', border:'1px solid rgba(59,130,246,0.12)',
      borderRadius:10, padding:'12px 16px', marginTop:4 }}>
      <button type='button' onClick={() => setOpen(o => !o)} style={{
        background:'none', border:'none', cursor:'pointer',
        display:'flex', alignItems:'center', gap:6,
        color:'var(--text-muted)', fontSize:11, fontWeight:600,
        textTransform:'uppercase', letterSpacing:0.6, padding:0, width:'100%'
      }}>
        <span style={{ fontSize:10 }}>{open ? '▾' : '▸'}</span>
        Additional Utilities
        <span style={{ marginLeft:6, fontSize:9, color:'var(--text-dim)', fontWeight:400,
          textTransform:'none', letterSpacing:0 }}>Water · Gas · Telecoms</span>
        {hasAny && !open && (
          <span style={{ marginLeft:6, fontSize:9, background:'rgba(59,130,246,0.15)',
            color:'#93c5fd', padding:'1px 6px', borderRadius:4 }}>data</span>
        )}
      </button>
      {open && (
        <div style={{ marginTop:12, display:'flex', flexDirection:'column', gap:4 }}>
          <UtilitySection label='Water'    prefix='water'    form={form} setVal={setVal} toInputDate={toInputDate} />
          <UtilitySection label='Gas'      prefix='gas'      form={form} setVal={setVal} toInputDate={toInputDate} />
          <UtilitySection label='Telecoms' prefix='telecoms' form={form} setVal={setVal} toInputDate={toInputDate} />
        </div>
      )}
    </div>
  );
}

export default function TenderModal({ tender, estimators, idnos, classifications, onSave, onClose, showTips = true, readOnly = false }) {
  const { user } = useUser();
  const isNew   = !tender?.id;
  const [stage, setStage] = useState(isNew ? 0 : getInitialStage(tender));
  const [form, setForm] = useState({
    project_ref:'', project_name:'', client:'', contact:'', email:'',
    date_received:'', deadline:'', estimator_user_id:'', planned_start_date:'',
    site_address:'', what3words:'', site_postcode:'',
    classification_id:'', idno_id:'', asset_value:'',
    estimate_ready_at:'', price:'',
    date_to_manager:'', date_approved:'',
    rejection_reason:'', rejection_count: 0,
    date_submitted:'',
    decision_date:'', status:'Bidding',
    lost_feedback:'', lost_feedback_at:'', lost_feedback_by:'',
    poc_status:'Not Required', poc_type:'', poc_application_date:'', poc_received_date:'',
    water_type:'', water_provider:'', water_application_date:'', water_received_date:'', water_notes:'',
    gas_type:'', gas_provider:'', gas_application_date:'', gas_received_date:'', gas_notes:'',
    telecoms_type:'', telecoms_provider:'', telecoms_application_date:'', telecoms_received_date:'', telecoms_notes:'',
    notes:'', new_note:'', initial_notes:'', submission_notes:'', outcome_notes:'',
    ...(tender || {}),
    status: normaliseStatus(tender?.status),
  });

  const isLocked = !isNew && ['Won','Lost','Dead'].includes(normaliseStatus(tender?.status));
  const isSubmissionLocked = !isNew && !form.date_approved && !['Won','Lost'].includes(form.status);

  // Rejection modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRejectionDetail, setShowRejectionDetail] = useState(false);
  const [showContractReview, setShowContractReview] = useState(false);
  const [rejectReason, setRejectReason]       = useState('');
  const [rejectError, setRejectError]         = useState('');
  const [showRepriceModal, setShowRepriceModal] = useState(false);
  const [repriceReason, setRepriceReason]       = useState('');
  const [repriceError, setRepriceError]         = useState('');

  // Lost feedback modal state
  const [showLostModal, setShowLostModal]     = useState(false);
  const [lostText, setLostText]               = useState(form.lost_feedback || '');

  const [saving, setSaving]             = useState(false);
  const [saved, setSaved]             = useState(false);
  const [savedAt, setSavedAt]         = useState(null);
  const [markingReady, setMarkingReady] = useState(false);
  const [ncCosts, setNcCosts] = useState([
    { nc_number: 1, nc_type: 'na', value_pence: null, value_input: '', authority_ref: '', descriptor: '' },
    { nc_number: 2, nc_type: 'na', value_pence: null, value_input: '', authority_ref: '', descriptor: '' },
    { nc_number: 3, nc_type: 'na', value_pence: null, value_input: '', authority_ref: '', descriptor: '' },
    { nc_number: 4, nc_type: 'na', value_pence: null, value_input: '', authority_ref: '', descriptor: '' },
  ]);
  const [ncSaving, setNcSaving] = useState(false);
  const [approving, setApproving]       = useState(false);
  const [error, setError]               = useState('');

  const set   = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setVal = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const selectedClass = classifications?.find(c => String(c.id) === String(form.classification_id));
  const showIDNO      = isConstructionType(selectedClass?.name);
  // SUR Phase 1: address / W3W requirements are decoupled from IDNO visibility.
  const addressRequired = requiresAddress(selectedClass?.name);
  const w3wRequired     = requiresW3W(selectedClass?.name);
  const selectedIDNO  = idnos?.find(i => String(i.id) === String(form.idno_id));
  const premium       = (Number(form.price)||0) + (Number(form.asset_value)||0);
  const isLost        = form.status === 'Lost';
  const missingLostFeedback = isLost && !form.lost_feedback;

  // Load NC costs when tender opens
  useEffect(() => {
    if (!tender?.id || isNew) return;
    import('../api').then(({ api }) => {
      api.get(`/api/commission/nc-costs/${tender.id}`)
        .then(r => {
          if (Array.isArray(r)) {
            setNcCosts(r.map(nc => ({
              ...nc,
              value_input: nc.value_pence ? (nc.value_pence / 100).toFixed(2) : '',
            })));
          }
        })
        .catch(() => {});
    });
  }, [tender?.id, isNew]);

  // Sync server-driven fields from tender prop into form when tender updates.
  // Covers: status transitions (new -> bid_review -> bidding), reference updates
  // (ENQ -> EST -> CN/CCN), and rejection metadata. User-edited fields (name,
  // scope, site, dates, prices) are preserved and never clobbered here.
  useEffect(() => {
    if (!tender) return;
    setForm(f => ({
      ...f,
      status:              normaliseStatus(tender.status),
      project_ref:         tender.project_ref || f.project_ref || '',
      enq_reference:       tender.enq_reference ?? f.enq_reference,
      est_reference:       tender.est_reference ?? f.est_reference,
      contract_reference:  tender.contract_reference ?? f.contract_reference,
      reference_type:      tender.reference_type ?? f.reference_type,
      contract_type:       tender.contract_type ?? f.contract_type,
      rejection_count:     tender.rejection_count ?? f.rejection_count,
      rejection_reason:    tender.rejection_reason ?? f.rejection_reason,
      rejected_by:         tender.rejected_by ?? f.rejected_by,
      rejected_at:         tender.rejected_at ?? f.rejected_at,
    }));
  }, [tender?.id, tender?.status, tender?.project_ref, tender?.reference_type]);


  const updateNcCost = (idx, field, value) => {
    setNcCosts(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const saveNcCosts = async (tenderId) => {
    if (!tenderId) return;
    try {
      const { api } = await import('../api');
      await api.patch(`/api/commission/nc-costs/${tenderId}`, {
        slots: ncCosts.map(nc => ({
          nc_number: nc.nc_number,
          nc_type: nc.nc_type,
          value_pence: nc.nc_type === 'value' && nc.value_input
            ? Math.round(parseFloat(nc.value_input) * 100) : null,
          authority_ref: nc.authority_ref || null,
          descriptor: nc.descriptor || null,
        }))
      });
    } catch(e) { console.error('NC save', e); }
  };

  const handleMarkReady = () => {
    setMarkingReady(true);
    const now = new Date().toISOString().slice(0,16);
    setForm(f => ({ ...f, estimate_ready_at: now, date_to_manager: now.slice(0,10), status:'Bidding' }));
    setTimeout(() => setMarkingReady(false), 600);
  };

  // ── Manager rejection ─────────────────────────────────────────────────────
  const handleReject = () => {
    if (!rejectReason.trim()) { setRejectError('A rejection reason is required.'); return; }
    const now = new Date().toISOString();
    setForm(f => ({
      ...f,
      status:           'Revision Required',
      rejection_reason:  rejectReason.trim(),
      rejection_count:   (Number(f.rejection_count)||0) + 1,
      rejected_at:       now,
      rejected_by:       user?.initials || '',
      date_approved:     '',
      estimate_ready_at: '',
      new_note:          `[REJECTED by ${user?.initials}] ${rejectReason.trim()}`,
    }));
    setShowRejectModal(false);
    setRejectReason('');
    setRejectError('');
    setStage(1); // send back to Estimate stage
  };

  const handleReprice = () => {
    if (!repriceReason.trim()) { setRepriceError('A reason is required.'); return; }
    const now = new Date().toISOString();
    const updated = {
      ...form,
      status:          'Bidding',
      rejection_count: (Number(form.rejection_count)||0) + 1,
      outcome_notes:   ((form.outcome_notes||'') + '\n[CLIENT RE-PRICE ' + now.slice(0,10) + '] ' + repriceReason.trim()).trim(),
      new_note:        '[CLIENT RE-PRICE by ' + (user?.initials||'') + '] ' + repriceReason.trim(),
    };
    setForm(updated);
    setShowRepriceModal(false);
    setRepriceReason('');
    setRepriceError('');
    // handleSave reads form state — defer one tick so setForm has landed
    setTimeout(() => onSave(updated), 50);
  };

  // ── Lost feedback save ────────────────────────────────────────────────────
  const handleSaveLostFeedback = () => {
    if (!lostText.trim()) return;
    const now = new Date().toISOString();
    setForm(f => ({
      ...f,
      lost_feedback:    lostText.trim(),
      lost_feedback_at: now,
      lost_feedback_by: user?.initials || '',
    }));
    setShowLostModal(false);
  };

  const handleSave = async () => {
    // Save NC costs alongside main save (fire and forget — non-blocking)
    const missing = [];
    if (!form.project_name || !form.project_name.trim()) missing.push('Project Name');
    if (!form.company_id) missing.push('Client / Company');
    if (isNew && !form.planned_start_date) missing.push('Planned Start Date');
    if (isNew && !form.deadline) missing.push('Submission Deadline');
    if (isNew && !form.date_received) missing.push('Date Received');
    if (isNew && !form.classification_id) missing.push('Work Classification');
    // SUR Phase 1: address required for construction + surveying;
    // W3W required only for construction.
    if (addressRequired && !form.site_address) missing.push('Site Address');
    if (w3wRequired     && !form.what3words)   missing.push('What3Words');
    if (form.poc_status === 'Application Required') {
      if (!form.poc_application_date) missing.push('POC Application Date');
      if (!form.poc_type) missing.push('POC Type');
    }
    if (missing.length > 0) {
      setError('Required fields missing: ' + missing.join(', '));
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = { ...form };
      ['estimator_user_id','classification_id','idno_id','asset_value','price'].forEach(f => {
        if (payload[f] === '' || payload[f] === null) payload[f] = null;
      });
      // Nullify empty date/timestamp fields to prevent DB errors
      ['date_received','deadline','planned_start_date','estimate_ready_at','date_to_manager',
       'date_approved','date_submitted','decision_date','poc_application_date','poc_received_date','water_application_date','water_received_date','gas_application_date','gas_received_date','telecoms_application_date','telecoms_received_date',
       'lost_feedback_at'].forEach(f => {
        if (payload[f] === '' || payload[f] === undefined) payload[f] = null;
      });
      if (!showIDNO) { payload.idno_id = null; payload.asset_value = null; }
      // Stage 2: Save must not auto-approve — only the explicit Approve button does that.
      if (stage === 2 && !tender?.date_approved) {
        payload.date_approved = null;
        if (payload.status === 'Bidding' || payload.status === 'bidding') {
          payload.status = tender?.status || 'Issued to Manager';
        }
      }
      const savedTender = await onSave(payload);
      // Save NC costs after tender is created/updated
      if (savedTender?.id) {
        try {
          await saveNcCosts(savedTender.id);
        } catch (ncErr) {
          console.error('NC save failed:', ncErr.message);
        }
      }
      setSaved(true);
      setSavedAt(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const fmtGBP = (v) => v ? `£${Number(v).toLocaleString('en-GB',{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—';

  return (
    <>
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'var(--card-bg)', borderRadius:16, width:'100%', maxWidth:900, maxHeight:'93vh', overflow:'auto', boxShadow:'0 32px 80px rgba(0,0,0,0.6)' }}>

        {/* Header */}
        <div style={{ padding:'20px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
          <div>
            <h2 style={{ color:'var(--text)', margin:0, fontSize:17, fontWeight:600 }}>{isNew ? 'New Tender' : form.project_name}</h2>
            <div style={{ display:'flex', gap:8, alignItems:'center', marginTop:3 }}>
              {form.project_ref && <span style={{ color:'var(--text-muted)', fontSize:12 }}>{form.project_ref}</span>}
              {form.rejection_count > 0 && (
                <span style={{ background:'rgba(249,115,22,0.15)', color:'#fb923c', border:'1px solid rgba(249,115,22,0.3)', borderRadius:5, padding:'1px 7px', fontSize:10, fontWeight:600 }}>
                  {form.rejection_count}× Revised
                </span>
              )}
              {form.status === 'Awaiting Decision' && (
                <span style={{ background:'rgba(217,119,6,0.15)', color:'#f59e0b', border:'1px solid rgba(217,119,6,0.35)', borderRadius:5, padding:'1px 7px', fontSize:10, fontWeight:600 }}>
                  ⚠ Awaiting Bid Decision
                </span>
              )}
              {missingLostFeedback && (
                <span style={{ background:'rgba(234,179,8,0.15)', color:'#eab308', border:'1px solid rgba(234,179,8,0.3)', borderRadius:5, padding:'1px 7px', fontSize:10, fontWeight:600 }}>
                  ⚠ Feedback required
                </span>
              )}
            </div>
          </div>
<div style={{ display:'flex', alignItems:'center', gap:8 }}>
            {readOnly && <span style={{ background:'rgba(245,158,11,0.15)', color:'#fbbf24', border:'1px solid rgba(245,158,11,0.3)', borderRadius:6, padding:'2px 8px', fontSize:10, fontWeight:600 }}>VIEW ONLY</span>}
            {!isNew && normaliseStatus(tender?.status) === 'Won' && (
              <button onClick={() => setShowContractReview(true)}
                style={{ padding:'4px 12px', background:'rgba(16,185,129,0.12)', border:'1px solid rgba(16,185,129,0.3)', borderRadius:6, fontSize:11, fontWeight:600, color:'#10b981', cursor:'pointer', fontFamily:'inherit' }}>
                📋 Contract Review
              </button>
            )}
            <button onClick={onClose} style={{ background:'var(--surface2)', border:'none', color:'var(--text-dim)', cursor:'pointer', padding:6, borderRadius:7, display:'flex', marginTop:2 }}><X size={18}/></button>
          </div>
        </div>

        {/* Stage nav */}
        <div style={{ padding:'14px 24px 0', display:'flex', gap:3 }}>
          {STAGES.map((s, i) => {
            const done   = i < stage;
            const active = i === stage;
            const tabLocked = (isLocked && i < 4) || (isSubmissionLocked && i === 3) || (!isNew && !form.deadline && i > 0) || (!isNew && i >= 2 && ((addressRequired && !form.site_address) || (w3wRequired && !form.what3words)));
            return (
              <button key={i} onClick={() => !isNew && !tabLocked && setStage(i)}
                title={isSubmissionLocked && i === 3 ? 'Manager approval required before submission' : s.desc}
                style={{ flex:1, padding:'7px 4px',
                  background: active ? '#3b82f6' : done ? '#1d4ed8' : 'rgba(255,255,255,0.05)',
                  border:'none', borderRadius:6, cursor: (isNew || tabLocked) ? 'default' : 'pointer',
                  color: active || done ? '#fff' : '#475569', fontSize:9.5,
                  fontWeight: active ? 700 : 500, textAlign:'center', lineHeight:1.3, transition:'all 0.15s',
                  opacity: tabLocked && !active ? 0.4 : 1 }}>
                <div>{done ? '✓' : i+1}</div>
                <div style={{ fontSize:9, marginTop:1, opacity:0.85 }}>{s.label}</div>
              </button>
            );
          })}
        </div>

        {/* Stage tip */}
        {showTips && (
          <div style={{ margin:'10px 24px 0', padding:'8px 12px', background:'rgba(59,130,246,0.07)', borderRadius:7, borderLeft:'2px solid #3b82f6' }}>
            <p style={{ color:'#93c5fd', fontSize:11, margin:0 }}>{STAGE_TIPS[stage]}</p>
          </div>
        )}

        {/* Revision Required banner */}
        {form.status === 'Revision Required' && (
          <div style={{ margin:'10px 24px 0', padding:'10px 14px', background:'rgba(249,115,22,0.1)', border:'1px solid rgba(249,115,22,0.25)', borderRadius:9 }}>
            <p style={{ color:'#fb923c', fontSize:12, fontWeight:600, margin:'0 0 3px', display:'flex', alignItems:'center', gap:5 }}>
              <AlertTriangle size={15}/> Returned for Revision
            </p>
            {form.rejection_reason && (
              <p style={{ color:'var(--text-dim)', fontSize:12, margin:0 }}>"{form.rejection_reason}"</p>
            )}
            {form.rejected_by && form.rejected_at && (
              <p style={{ color:'var(--text-muted)', fontSize:11, margin:'3px 0 0' }}>
                — {form.rejected_by}, {new Date(form.rejected_at).toLocaleString('en-GB',{dateStyle:'medium',timeStyle:'short'})}
              </p>
            )}
          </div>
        )}

        {/* Lost feedback banner */}
        {isLost && (
          <div style={{ margin:'10px 24px 0', padding:'10px 14px',
            background: form.lost_feedback ? 'rgba(16,185,129,0.07)' : 'rgba(234,179,8,0.08)',
            border: `1px solid ${form.lost_feedback ? 'rgba(16,185,129,0.2)' : 'rgba(234,179,8,0.25)'}`,
            borderRadius:9 }}>
            {form.lost_feedback ? (
              <>
                <p style={{ color:'#10b981', fontSize:12, fontWeight:600, margin:'0 0 3px', display:'flex', alignItems:'center', gap:5 }}>
                  <MessageSquare size={15}/> Client Feedback Recorded
                </p>
                <p style={{ color:'var(--text-dim)', fontSize:12, margin:'0 0 3px' }}>"{form.lost_feedback}"</p>
                <button onClick={() => { setLostText(form.lost_feedback); setShowLostModal(true); }}
                  style={{ background:'none', border:'none', color:'var(--text-muted)', fontSize:11, cursor:'pointer', textDecoration:'underline', padding:0 }}>
                  Edit feedback
                </button>
              </>
            ) : (
              <>
                <p style={{ color:'#eab308', fontSize:12, fontWeight:600, margin:'0 0 4px', display:'flex', alignItems:'center', gap:5 }}>
                  <AlertTriangle size={15}/> Client feedback required
                </p>
                <p style={{ color:'var(--text-dim)', fontSize:12, margin:'0 0 8px' }}>
                  Add feedback from the client or prospect about why this tender was lost. This can be added now or later.
                </p>
                <button onClick={() => setShowLostModal(true)}
                  style={{ padding:'6px 14px', background:'rgba(234,179,8,0.15)', border:'1px solid rgba(234,179,8,0.3)', borderRadius:7, color:'#eab308', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                  Add Feedback Now
                </button>
              </>
            )}
          </div>
        )}

        {/* Status pills — visible on all tabs once tender exists */}
        {!isNew && (
          <div style={{ padding:'10px 24px 0', display:'flex', gap:5, flexWrap:'wrap' }}>
            {STATUS_OPTIONS.filter(s => s !== 'Revision Required').map(s => (
              <button key={s} onClick={() => setVal('status', s)} disabled={['Paused','Dead'].includes(s) && !['director','manager'].includes(user?.role)}
                style={{ padding:'3px 10px', border:`1px solid ${form.status===s ? STATUS_META[s]?.color||'#64748b' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius:20, fontSize:11,
                  background: form.status===s ? STATUS_META[s]?.bg||'#f3f4f6' : 'transparent',
                  color: form.status===s ? STATUS_META[s]?.text||'#374151' : '#64748b', cursor:'pointer' }}>
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Form body */}
        <div style={{ padding:'18px 24px' }}>
          {error && <div style={{ background:'rgba(239,68,68,0.15)', color:'#fca5a5', border:'1px solid rgba(239,68,68,0.3)', padding:'10px 14px', borderRadius:8, fontSize:13, marginBottom:16 }}>{error}</div>}

          {isLocked && (
            <div style={{ margin:'0 0 12px', padding:'9px 14px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:8, display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:14 }}>🔒</span>
              <span style={{ color:'#fca5a5', fontSize:12 }}>This tender is {normaliseStatus(tender?.status)} — historical stages are locked.</span>
            </div>
          )}
          {/* ── Stage 0: Initial Entry ── */}
          {stage === 0 && (
            <div style={{ pointerEvents: isLocked ? 'none' : 'auto', opacity: isLocked ? 0.6 : 1 }}>
            <>
              <div style={{ display:'flex', gap:24, alignItems:'flex-start' }}>
                <div style={{ flex:1, minWidth:0 }}>
                {form.status === 'Awaiting Decision' && user?.role && ['manager','director'].includes(user.role) && (
                  <div style={{ marginBottom:14, padding:'10px 14px',
                    background:'rgba(217,119,6,0.1)',
                    border:'1px solid rgba(217,119,6,0.3)',
                    borderRadius:9,
                    display:'flex', alignItems:'flex-start', gap:8 }}>
                    <span style={{ fontSize:16, lineHeight:1 }}>⚠</span>
                    <div style={{ fontSize:12, color:'var(--text)', lineHeight:1.5 }}>
                      <strong style={{ color:'#f59e0b' }}>Bid/no-bid decision required.</strong>
                      {' '}Complete the fields below, then use the <strong>Bid Decision</strong> panel on the right to approve or decline (ISO requirement).
                    </div>
                  </div>
                )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Input label="Project Ref" value={form.project_ref || ''} onChange={() => {}} placeholder="Auto-generated" disabled showTips={showTips} tip="Auto-generated IPE reference number." />
                <Select label="Estimator" value={form.estimator_user_id} onChange={set('estimator_user_id')} showTips={showTips} tip="Assign the estimator responsible for this bid.">
                  <option value="">— Unassigned —</option>
                  {estimators?.map(e => <option key={e.id} value={e.id}>{e.initials} — {e.full_name}</option>)}
                </Select>
              </div>
              <Input label="Project Name" value={form.project_name} onChange={set('project_name')} required placeholder="e.g. Wareham Substation" showTips={showTips} tip="Descriptive name for the project." />
              <CompanyTypeahead
                value={form.client}
                companyId={form.company_id}
                onChange={(id, name) => setForm(f => ({ ...f, company_id: id, client: name }))}
                onClear={() => setForm(f => ({ ...f, company_id: null, client: '', primary_contact_id: null, contact: '', email: '' }))}
                disabled={isLocked}
                showTips={showTips}
                required
              />
              <ContactTypeahead
                value={form.contact}
                contactId={form.primary_contact_id}
                companyId={form.company_id}
                onChange={(id, name) => setForm(f => ({ ...f, primary_contact_id: id, contact: name }))}
                onClear={() => setForm(f => ({ ...f, primary_contact_id: null, contact: '' }))}
                disabled={isLocked}
              />
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Input label="Date Received" type="date" value={toInputDate(form.date_received)} onChange={set('date_received')} required showTips={showTips} tip="Date the tender invitation was received." />
                <Input label="Submission Deadline" type="date" value={toInputDate(form.deadline)} onChange={set('deadline')} required showTips={showTips} tip="Client's deadline. Overdue alerts trigger from this date if still at Bidding status." />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Input label="Planned Start Date" type="date" value={toInputDate(form.planned_start_date)} onChange={set('planned_start_date')} required showTips={showTips} tip="Expected project start date. Used for resource and cash flow planning. Required." />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5, display:'block', marginBottom:4 }}>Site Address{showIDNO && <span style={{color:'#ef4444'}}> *</span>}</label>
                  <textarea value={form.site_address||''} onChange={set('site_address')} rows={2} placeholder="Full site address..." style={{ width:'100%', background:'var(--input-bg)', border:'1px solid var(--input-border)', borderRadius:6, padding:'8px 10px', color:'var(--text)', fontSize:13, fontFamily:'inherit', resize:'vertical', boxSizing:'border-box' }} />
                </div>
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5, display:'block', marginBottom:4 }}>What3Words{showIDNO && <span style={{color:'#ef4444'}}> *</span>}</label>
                  <input value={form.what3words||''} onChange={e => setVal('what3words', e.target.value.toLowerCase().replace(/[^a-z.]/g,''))} placeholder="word.word.word" style={{ width:'100%', background:'var(--input-bg)', border:'1px solid var(--input-border)', borderRadius:6, padding:'8px 10px', color:'var(--text)', fontSize:13, boxSizing:'border-box' }} />
                  <label style={{ fontSize:11, fontWeight:600, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:0.5, display:'block', marginTop:8, marginBottom:4 }}>Postcode</label>
                  <input value={form.site_postcode||''} onChange={e => setVal('site_postcode', e.target.value.toUpperCase())} placeholder="e.g. M1 1AE" style={{ width:'100%', background:'var(--input-bg)', border:'1px solid var(--input-border)', borderRadius:6, padding:'8px 10px', color:'var(--text)', fontSize:13, boxSizing:'border-box' }} />
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <Select label="Work Classification" value={form.classification_id} onChange={set('classification_id')} required showTips={showTips} tip="IDNO/AV fields are only available for construction projects, not Consultancy/Design.">
                  <option value="">— Select classification —</option>
                  {classifications?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </div>
              <Textarea label="Scope of Works" value={form.initial_notes||''} onChange={set('initial_notes')} placeholder="e.g. Supply and install 500kVA substation at industrial unit, including termination and SAP witnessing (min 20 chars)" rows={3} showTips={showTips} tip="Please provide a detailed scope of works, including load information, project requirements etc." />

              {/* POC Status */}
              <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:10, padding:'14px 16px' }}>
                <p style={{ color:'#93c5fd', fontSize:11, fontWeight:600, margin:'0 0 10px', textTransform:'uppercase', letterSpacing:0.6 }}>Point of Connection (POC)</p>
                <Select label="POC Status" value={form.poc_status} onChange={set('poc_status')} showTips={showTips} tip="POC required, already received (pre-existing), or application needed?">
                  <option value="Not Required">Not Required</option>
                  <option value="Application Required">Application Required — awaiting DNO</option>
                  <option value="Already Received">Already Received (pre-existing POC)</option>
                </Select>
                {form.poc_status === 'Application Required' && (
                  <>
                    <Select label="POC Type" value={form.poc_type} onChange={(e) => {
                      setVal('poc_type', e.target.value);
                      // POC Type is mandatory when Application Required, so if user clears Application Date, warn
                      if (e.target.value && !form.poc_application_date) {
                        // POC Type set, but Application Date missing — will be caught on save
                      }
                    }} showTips={showTips} tip="Select the type of POC application — determines the DNO working day target.">
                      <option value="">— Select type —</option>
                      <option value="LV Demand">LV Demand (25 working days)</option>
                      <option value="HV Demand">HV Demand (35 working days)</option>
                      <option value="EHV Demand">EHV Demand (65 working days)</option>
                      <option value="LV Generation">LV Generation (45 working days)</option>
                      <option value="HV Generation">HV Generation (65 working days)</option>
                      <option value="EHV Generation">EHV Generation (65 working days)</option>
                    </Select>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                      <Input label="POC Application Date" type="date" value={toInputDate(form.poc_application_date)} onChange={(e) => {
                        setVal('poc_application_date', e.target.value);
                        // Application Date is mandatory when POC Type is set
                      }} showTips={showTips} tip="Date POC application was submitted to DNO." />
                      <Input label="POC Received Date" type="date" value={toInputDate(form.poc_received_date)} onChange={(e) => {
                        setVal('poc_received_date', e.target.value);
                        // When POC Received Date is set, auto-update POC Status to 'Received'
                        if (e.target.value && form.poc_status === 'Application Required') {
                          setVal('poc_status', 'Received');
                        }
                      }} showTips={showTips} tip="Date POC response was received from DNO. Fill in when received to start the estimator clock." />
                    </div>
                  </>
                )}
              </div>
              {/* Additional Utilities */}
              <AdditionalUtilities form={form} setVal={setVal} toInputDate={toInputDate} showTips={showTips} />
                </div>
                <BidNoBidSidebar tender={{ ...tender, ...form }} user={user} onDecided={(updated) => onSave && onSave(updated)} />
              </div>
            </>
            </div>
          )}

          {/* ── Stage 1: Estimate + IDNO/AV ── */}
          {stage === 1 && (
            <div style={{ pointerEvents: isLocked ? 'none' : 'auto', opacity: isLocked ? 0.6 : 1 }}>
            <>
              <Input label="Estimate Price (£)" type="number" value={form.price ?? ""} onChange={set('price')} placeholder="0.00" required showTips={showTips} tip="Net estimate value to be submitted to the client." />

              {/* ── Non-Contestable Costs ── */}
              <div style={{ background:'rgba(217,119,6,0.06)', border:'1px solid rgba(217,119,6,0.2)', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
                  <p style={{ color:'#f59e0b', fontSize:11, fontWeight:600, margin:0, textTransform:'uppercase', letterSpacing:0.8 }}>
                    Non-Contestable Costs (NC)
                  </p>
                  {ncCosts.some(nc => nc.nc_type === 'value' && nc.value_pence) && (
                    <span style={{ fontSize:11, color:'#f59e0b', fontFamily:"'DM Mono', monospace" }}>
                      Total deduction: £{ncCosts.filter(nc => nc.nc_type === 'value' && nc.value_pence).reduce((s, nc) => s + (nc.value_pence / 100), 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
                {ncCosts.map((nc, idx) => (
                  <div key={nc.nc_number} style={{ marginBottom: idx < 3 ? 10 : 0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4 }}>
                      <span style={{ fontSize:10, fontWeight:700, color:'var(--text-dim)', width:28, flexShrink:0 }}>NC{nc.nc_number}</span>
                      <select
                        value={nc.nc_type}
                        onChange={e => updateNcCost(idx, 'nc_type', e.target.value)}
                        style={{ fontSize:11, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', flex:'0 0 160px' }}>
                        <option value="na">N/A</option>
                        <option value="client_paid_direct">Client Paid Direct</option>
                        <option value="value">Value</option>
                      </select>
                      {nc.nc_type === 'value' && (
                        <input
                          type="number"
                          placeholder="0.00"
                          value={nc.value_input ?? ''}
                          onChange={e => updateNcCost(idx, 'value_input', e.target.value)}
                          onBlur={e => updateNcCost(idx, 'value_pence', e.target.value ? Math.round(parseFloat(e.target.value) * 100) : null)}
                          style={{ fontSize:11, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', width:100 }}
                        />
                      )}
                    </div>
                    {nc.nc_type === 'value' && (
                      <div style={{ display:'flex', gap:6, paddingLeft:34 }}>
                        <input
                          type="text"
                          placeholder="Authority ref (e.g. UKPN-2026-00123)"
                          value={nc.authority_ref ?? ''}
                          onChange={e => updateNcCost(idx, 'authority_ref', e.target.value)}
                          style={{ fontSize:11, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', flex:1 }}
                        />
                        <input
                          type="text"
                          placeholder="Description"
                          value={nc.descriptor ?? ''}
                          onChange={e => updateNcCost(idx, 'descriptor', e.target.value)}
                          style={{ fontSize:11, padding:'4px 8px', borderRadius:6, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', flex:1 }}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {form.price && ncCosts.some(nc => nc.nc_type === 'value' && nc.value_pence) && (
                  <div style={{ marginTop:10, paddingTop:8, borderTop:'1px solid rgba(217,119,6,0.2)' }}>
                    <p style={{ fontSize:11, color:'#f59e0b', margin:0, fontFamily:"'DM Mono', monospace" }}>
                      TGPV = £{Math.max(0, parseFloat(form.price || 0) - ncCosts.filter(nc => nc.nc_type === 'value' && nc.value_pence).reduce((s, nc) => s + (nc.value_pence / 100), 0)).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      
                    </p>
                  </div>
                )}
              </div>

              {showIDNO && !readOnly && (
                <div style={{ background:'rgba(59,130,246,0.07)', border:'1px solid rgba(59,130,246,0.18)', borderRadius:10, padding:'14px 16px', marginBottom:14 }}>
                  <p style={{ color:'#60a5fa', fontSize:11, fontWeight:600, margin:'0 0 10px', textTransform:'uppercase', letterSpacing:0.8, display:'flex', alignItems:'center', gap:5 }}>
                    IDNO / Asset Value
                    <Tip show={showTips} text="Record if an IDNO has provided an AV offer during estimate preparation. The AV will be received upon energisation if the project is won. Not applicable to Consultancy/Design." />
                  </p>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                    <Select label="IDNO" value={form.idno_id} onChange={set('idno_id')} showTips={false}>
                      <option value="">— None —</option>
                      {idnos?.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </Select>
                    <Input label="Asset Value (£)" type="number" value={form.asset_value ?? ""} onChange={set('asset_value')} placeholder="0.00" disabled={!form.idno_id} showTips={showTips} tip="AV offered by the IDNO. Received on energisation if won." />
                  </div>
                  {form.idno_id && form.price && form.asset_value > 0 && (
                    <div style={{ background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.18)', borderRadius:7, padding:'8px 12px', marginTop:4 }}>
                      <p style={{ color:'#6ee7b7', fontSize:12, margin:0 }}>
                        {fmtGBP(form.price)} estimate + {fmtGBP(form.asset_value)} AV ({selectedIDNO?.name}) = <strong>Premium: {fmtGBP(premium)}</strong>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Ready for review button */}
              {form.estimate_ready_at ? (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 14px', background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.2)', borderRadius:9 }}>
                  <CheckCircle size={17} color="#10b981" />
                  <div style={{ flex:1 }}>
                    <p style={{ color:'#10b981', fontSize:12, fontWeight:600, margin:0 }}>Marked ready for review</p>
                    <p style={{ color:'var(--text-muted)', fontSize:11, margin:'1px 0 0' }}>
                      {new Date(form.estimate_ready_at).toLocaleString('en-GB',{dateStyle:'medium',timeStyle:'short'})}
                    </p>
                  </div>
                  <button onClick={() => setForm(f => ({ ...f, estimate_ready_at:'', date_to_manager:'' }))}
                    style={{ background:'none', border:'none', color:'var(--text-dim)', fontSize:11, cursor:'pointer', textDecoration:'underline' }}>
                    Undo
                  </button>
                </div>
              ) : (
                <>
                  <button onClick={handleMarkReady} disabled={!form.price || markingReady}
                    style={{ width:'100%', padding:'11px', marginTop:4,
                      background: !form.price ? 'rgba(255,255,255,0.04)' : 'rgba(16,185,129,0.15)',
                      border:`1px solid ${!form.price ? 'rgba(255,255,255,0.08)' : 'rgba(16,185,129,0.3)'}`,
                      borderRadius:9, color: !form.price ? '#475569' : '#10b981',
                      fontSize:13, fontWeight:600, cursor: !form.price ? 'not-allowed' : 'pointer',
                      display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
                    <CheckCircle size={17}/>{markingReady ? 'Marking…' : 'Estimate Ready — Send for Review'}
                  </button>
                  {!form.price && <p style={{ color:'var(--text-dim)', fontSize:11, marginTop:5, textAlign:'center' }}>Enter estimate price first</p>}
                </>
              )}
            </>
            </div>
          )}
          {/* ── Stage 2: Manager Review ── */}
          {stage === 2 && (
            <div style={{ pointerEvents: isLocked ? 'none' : 'auto', opacity: isLocked ? 0.6 : 1 }}>
            <>
              {form.estimate_ready_at && (
                <div style={{ marginBottom:14, padding:'9px 12px', background:'var(--surface2)', borderRadius:8, cursor:'pointer' }} onClick={() => setShowRejectionDetail(true)}>
                  <p style={{ color:'var(--text-muted)', fontSize:11, margin:0 }}>
                    Submitted for review: <span style={{ color:'var(--text-dim)' }}>{new Date(form.estimate_ready_at).toLocaleString('en-GB',{dateStyle:'medium',timeStyle:'short'})}</span>
                  </p>
                </div>
              )}
              {/* Date approved is set automatically by the Approve button — not manually editable */}
              {form.date_approved && (
                <p style={{ fontSize:11, color:'#10b981', marginBottom:10 }}>Approved: {form.date_approved}</p>
              )}

              {/* Approve / Reject actions */}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:4 }}>
                <button onClick={async () => {
                  if (approving || form.date_approved) return;
                  setApproving(true);
                  const today = new Date().toISOString().slice(0,10);
                  const updated = { ...form, date_approved: form.date_approved || today, status: 'Bidding' };
                  setForm(updated);
                  try { await onSave(updated); setStage(3); } catch(e) { console.error(e); }
                  setTimeout(() => setApproving(false), 800);
                }} disabled={approving || !!form.date_approved}
                style={{ padding:'10px',
                  background: form.date_approved ? 'rgba(16,185,129,0.3)' : approving ? 'rgba(16,185,129,0.3)' : 'rgba(16,185,129,0.12)',
                  border: '1px solid rgba(16,185,129,0.3)',
                  borderRadius:8, color:'#10b981', fontSize:12, fontWeight:600,
                  cursor: (approving || form.date_approved) ? 'default' : 'pointer',
                  opacity: form.date_approved ? 0.5 : 1,
                  display:'flex', alignItems:'center', justifyContent:'center', gap:6,
                  transition:'all 0.2s' }}>
                  <CheckCircle size={16}/> {form.date_approved ? 'Approved ✓' : approving ? 'Approving…' : 'Approve'}
                </button>
                <button onClick={() => { if (!form.date_approved) setShowRejectModal(true); }}
                  disabled={!!form.date_approved}
                  style={{ padding:'10px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8, color:'#f87171', fontSize:12, fontWeight:600, cursor: form.date_approved ? 'default' : 'pointer', opacity: form.date_approved ? 0.4 : 1, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <AlertTriangle size={16}/> Reject & Return
                </button>
              </div>

              {form.rejection_count > 0 && (
                <div style={{ marginTop:12, padding:'9px 12px', background:'rgba(249,115,22,0.07)', border:'1px solid rgba(249,115,22,0.2)', borderRadius:8, cursor:'pointer' }} onClick={() => setShowRejectionDetail(true)}>
                  <p style={{ color:'#fb923c', fontSize:11, margin:'0 0 2px', fontWeight:600 }}>Previous rejection ({form.rejection_count}×) — Click to view</p>
                  <p style={{ color:'var(--text)', fontSize:11, margin:0, maxHeight:40, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{form.rejection_reason || '(No reason provided)'}</p>
                </div>
              )}
            </>
            </div>
          )}
          {/* ── Stage 3: Submission ── */}
          {stage === 3 && (
            <div style={{ pointerEvents: (isLocked || isSubmissionLocked) ? 'none' : 'auto', opacity: (isLocked || isSubmissionLocked) ? 0.6 : 1 }}>
            <>
              <Input label="Date Submitted to Client" type="date" value={toInputDate(form.date_submitted)} onChange={(e) => {
                setVal('date_submitted', e.target.value);
                // Auto-update status to Submitted-Awaiting if bidding and date is set
                if (e.target.value && (form.status === 'Bidding' || form.status === 'bidding')) {
                  setVal('status', 'Submitted-Awaiting');
                }
              }} required showTips={showTips} tip="Date the tender was formally submitted to the client." />
              <Textarea label="Submission Notes" value={form.submission_notes||''} onChange={set('submission_notes')} placeholder="Notes on submission, cover letter, clarifications…" rows={3} showTips={showTips} />
              <div style={{ background:'var(--surface2)', borderRadius:8, padding:'12px 14px', marginTop:4 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ color:'var(--text-dim)', fontSize:12 }}>Estimate Value</span>
                  <span style={{ color:'var(--text)', fontSize:12 }}>{fmtGBP(form.price)}</span>
                </div>
                {form.idno_id && form.asset_value && (
                  <>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                      <span style={{ color:'var(--text-dim)', fontSize:12 }}>Asset Value ({selectedIDNO?.name})</span>
                      <span style={{ color:'#60a5fa', fontSize:12 }}>{fmtGBP(form.asset_value)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', borderTop:'1px solid var(--border)', paddingTop:6 }}>
                      <span style={{ color:'#10b981', fontSize:12, fontWeight:600 }}>Premium Value</span>
                      <span style={{ color:'#10b981', fontSize:12, fontWeight:600 }}>{fmtGBP(premium)}</span>
                    </div>
                  </>
                )}
              </div>
              {/* ── Retrospective AV entry ── */}
            </>
            </div>
          )}
          {/* ── Retrospective AV entry — outside submission lock ── */}
          {stage === 3 && (
            (['Bidding','Submitted-Awaiting'].includes(form.status) && ['estimator','manager','director'].includes(user?.role)) ||
            (form.status === 'Won' && ['manager','director'].includes(user?.role))
          ) && showIDNO && (
            <div style={{ marginTop:10, padding:'12px 14px', background:'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.18)', borderRadius:8 }}>
              <p style={{ color:'#60a5fa', fontSize:11, fontWeight:600, margin:'0 0 10px', textTransform:'uppercase', letterSpacing:0.6, display:'flex', alignItems:'center', gap:6 }}>
                IDNO / Asset Value
                <span style={{ fontWeight:400, textTransform:'none', fontSize:10, color:'var(--text-dim)', letterSpacing:0 }}>— update if AV received after submission</span>
              </p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>IDNO</label>
                  <select value={form.idno_id||''} onChange={set('idno_id')}
                    style={{ width:'100%', padding:'8px 10px', background:'var(--input-bg)', border:'1px solid var(--border)', borderRadius:7, color:'var(--text)', fontSize:13, boxSizing:'border-box' }}>
                    <option value="">— None —</option>
                    {idnos?.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>Asset Value (£)</label>
                  <input type="number" value={form.asset_value ?? ''} onChange={set('asset_value')}
                    placeholder="0.00" disabled={!form.idno_id}
                    style={{ width:'100%', padding:'8px 10px',
                      background: form.idno_id ? 'var(--input-bg)' : 'var(--surface2)',
                      border:'1px solid var(--border)', borderRadius:7,
                      color: form.idno_id ? 'var(--text)' : 'var(--text-dim)',
                      fontSize:13, boxSizing:'border-box' }} />
                </div>
              </div>
              {form.idno_id && form.price && Number(form.asset_value) > 0 && (
                <div style={{ marginTop:8, padding:'8px 10px', background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.18)', borderRadius:6 }}>
                  <p style={{ color:'#6ee7b7', fontSize:12, margin:0 }}>
                    {fmtGBP(form.price)} + {fmtGBP(form.asset_value)} AV ({selectedIDNO?.name}) = <strong>Premium: {fmtGBP(premium)}</strong>
                  </p>
                </div>
              )}
            </div>
          )}
          {/* ── Stage 4: Outcome ── */}
          {stage === 4 && (
            <>
              {['manager','director'].includes(user?.role) && (form.status === 'Submitted-Awaiting' || form.status === 'submitted') && (
                <div style={{ marginBottom:14, padding:'10px 14px', background:'rgba(234,179,8,0.06)', border:'1px solid rgba(234,179,8,0.2)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <div>
                    <p style={{ color:'#fbbf24', fontSize:11, fontWeight:600, margin:'0 0 2px', textTransform:'uppercase', letterSpacing:0.5 }}>Client Re-price Request</p>
                    <p style={{ color:'var(--text-dim)', fontSize:11, margin:0 }}>Client has requested a revised price — returns tender to Bidding.</p>
                  </div>
                  <button onClick={() => setShowRepriceModal(true)}
                    style={{ padding:'7px 14px', background:'rgba(234,179,8,0.15)', border:'1px solid rgba(234,179,8,0.35)', borderRadius:7, color:'#fbbf24', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', marginLeft:12 }}>
                    Re-price
                  </button>
                </div>
              )}
              <Input label="Decision Date" type="date" value={toInputDate(form.decision_date)} onChange={set('decision_date')} required showTips={showTips} tip="Date the client confirmed the outcome." />
              <Textarea label="Outcome Notes" value={form.outcome_notes||''} onChange={set('outcome_notes')} placeholder="Notes on outcome, feedback, lessons learned…" rows={3} showTips={showTips} />
              <Select label="Final Status" value={(form.date_submitted && form.status === 'Bidding') ? 'Submitted-Awaiting' : form.status} onChange={set('status')} required showTips={showTips} tip="Won/Lost are used in win rate calculations. Only decided bids (Won or Lost) count toward the win rate.">
                {['Bidding','Submitted-Awaiting','Won','Lost','Paused','Dead'].filter(s => !['Paused','Dead'].includes(s) || ['director','manager'].includes(user?.role)).map(s => <option key={s} value={s}>{s}</option>)}
              </Select>
              <div style={{ background:'var(--surface2)', borderRadius:8, padding:'12px 14px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ color:'var(--text-dim)', fontSize:12 }}>Estimate</span>
                  <span style={{ color:'var(--text)', fontSize:12 }}>{fmtGBP(form.price)}</span>
                </div>
                {form.idno_id && form.asset_value && (
                  <>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                      <span style={{ color:'var(--text-dim)', fontSize:12 }}>AV ({selectedIDNO?.name})</span>
                      <span style={{ color:'#60a5fa', fontSize:12 }}>{fmtGBP(form.asset_value)}</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', borderTop:'1px solid var(--border)', paddingTop:6 }}>
                      <span style={{ color:'#10b981', fontSize:12, fontWeight:600 }}>Premium</span>
                      <span style={{ color:'#10b981', fontSize:12, fontWeight:600 }}>{fmtGBP(premium)}</span>
                    </div>
                  </>
                )}
              </div>
              {/* ── Record Corrections — Manager/Director, Won only ── */}
              {form.status === 'Won' && ['manager','director'].includes(user?.role) && (
                <div style={{ marginTop:12, padding:'12px 14px', background:'rgba(251,191,36,0.06)', border:'1px solid rgba(251,191,36,0.2)', borderRadius:8 }}>
                  <p style={{ color:'#fbbf24', fontSize:11, fontWeight:600, margin:'0 0 10px', textTransform:'uppercase', letterSpacing:0.6, display:'flex', alignItems:'center', gap:6 }}>
                    Record Corrections
                    <span style={{ fontWeight:400, textTransform:'none', fontSize:10, color:'var(--text-dim)', letterSpacing:0 }}>— post-award amendments only</span>
                  </p>
                  <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                    <div>
                      <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>Final Contract Value (£) <span style={{ fontSize:10, color:'var(--text-dim)' }}>overrides estimate</span></label>
                      <input type='number' value={form.price ?? ''} onChange={set('price')} placeholder='0.00'
                        style={{ width:'100%', padding:'8px 10px', background:'var(--input-bg)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:7, color:'var(--text)', fontSize:13, boxSizing:'border-box' }} />
                    </div>
                    <div>
                      <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>IDNO</label>
                      <select value={form.idno_id||''} onChange={set('idno_id')}
                        style={{ width:'100%', padding:'8px 10px', background:'var(--input-bg)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:7, color:'var(--text)', fontSize:13, boxSizing:'border-box' }}>
                        <option value=''>— None —</option>
                        {idnos?.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginTop:10 }}>
                    <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:4 }}>Asset Value (£) <span style={{ fontSize:10, color:'var(--text-dim)' }}>late IDNO offer</span></label>
                    <input type='number' value={form.asset_value ?? ''} onChange={set('asset_value')} placeholder='0.00' disabled={!form.idno_id}
                      style={{ width:'100%', padding:'8px 10px', background: form.idno_id ? 'var(--input-bg)' : 'var(--surface2)', border:'1px solid rgba(251,191,36,0.3)', borderRadius:7, color: form.idno_id ? 'var(--text)' : 'var(--text-dim)', fontSize:13, boxSizing:'border-box' }} />
                  </div>
                  {form.price && (
                    <div style={{ marginTop:8, padding:'8px 10px', background:'rgba(16,185,129,0.08)', border:'1px solid rgba(16,185,129,0.18)', borderRadius:6 }}>
                      <p style={{ color:'#6ee7b7', fontSize:12, margin:0 }}>
                        Contract: {fmtGBP(form.price)}
                        {form.idno_id && Number(form.asset_value) > 0 && (
                          <> + {fmtGBP(form.asset_value)} AV ({selectedIDNO?.name}) = <strong>Premium: {fmtGBP(premium)}</strong></>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {/* Notes (existing tenders, stages 0/3/4) */}
          {!isNew && [0,3,4].includes(stage) && (
            <div style={{ marginTop:16, borderTop:'1px solid var(--border)', paddingTop:14 }}>
              <label style={{ display:'block', color:'var(--text-dim)', fontSize:12, marginBottom:6 }}>
                Add Note
                <Tip show={showTips} text="Notes are appended with your initials and a timestamp. Visible in the activity log." />
              </label>
              {form.notes && (
                <div style={{ background:'rgba(0,0,0,0.2)', borderRadius:7, padding:'9px 12px', marginBottom:8, maxHeight:80, overflow:'auto' }}>
                  <pre style={{ color:'var(--text-muted)', fontSize:11, margin:0, whiteSpace:'pre-wrap', fontFamily:'inherit' }}>{form.notes}</pre>
                </div>
              )}
              <textarea value={form.new_note||''} rows={2} onChange={set('new_note')} placeholder="Add a note…"
                style={{ width:'100%', padding:'9px 12px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text)', fontSize:13, boxSizing:'border-box', resize:'vertical', fontFamily:'inherit' }} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 24px 18px', borderTop:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', background:'var(--card-bg)', position:'sticky', bottom:0 }}>
          <div style={{ display:'flex', gap:6 }}>
            {stage > 0 && (
              <button onClick={() => setStage(s => s-1)}
                style={{ padding:'8px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                <ChevronLeft size={15}/> Back
              </button>
            )}
            {stage < 4 && !isNew && !(isSubmissionLocked && stage === 2) && (
              <button onClick={() => setStage(s => s+1)}
                style={{ padding:'8px 14px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                Next <ChevronRight size={15}/>
              </button>
            )}
          </div>
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={onClose} style={{ padding:'8px 16px', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, cursor:'pointer' }}>Cancel</button>
            <button onClick={readOnly ? onClose : handleSave} disabled={saving && !readOnly}
              style={{ padding:'8px 20px', background: saving ? 'rgba(59,130,246,0.4)' : '#3b82f6', border:'none', borderRadius:8, color:'#fff', fontSize:13, fontWeight:600, cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : savedAt ? `✓ Saved ${savedAt.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' })}` : readOnly ? 'Close' : (isNew ? 'Create Tender' : 'Save Changes')}
            </button>
          </div>
        </div>
      </div>
    </div>

    {/* ── Rejection modal ───────────────────────────────────────────────────── */}
    {showRejectModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <div style={{ background:'var(--card-bg)', borderRadius:14, width:'100%', maxWidth:440, padding:24, boxShadow:'0 24px 60px rgba(0,0,0,0.6)' }}>
          <h3 style={{ color:'#f87171', margin:'0 0 6px', fontSize:16, display:'flex', alignItems:'center', gap:7 }}>
            <AlertTriangle size={18}/> Reject & Return to Estimator
          </h3>
          <p style={{ color:'var(--text-muted)', fontSize:12, margin:'0 0 16px' }}>
            A written reason is required before this tender can be returned. The reason will be visible to the estimator and recorded in the activity log.
          </p>
          <Textarea label="Rejection Reason" required value={rejectReason} onChange={e => { setRejectReason(e.target.value); setRejectError(''); }}
            placeholder="Explain what needs to be revised…" rows={4} showTips={false} />
          {rejectError && <p style={{ color:'#f87171', fontSize:12, margin:'-8px 0 12px' }}>{rejectError}</p>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
            <button onClick={() => { setShowRejectModal(false); setRejectReason(''); setRejectError(''); }}
              style={{ padding:'8px 16px', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={handleReject}
              style={{ padding:'8px 18px', background:'rgba(239,68,68,0.2)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:8, color:'#f87171', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Confirm Rejection
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Client re-price modal ────────────────────────────────────────────── */}
    {showRepriceModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <div style={{ background:'var(--card-bg)', borderRadius:14, width:'100%', maxWidth:440, padding:24, boxShadow:'0 24px 60px rgba(0,0,0,0.6)' }}>
          <h3 style={{ color:'#fbbf24', margin:'0 0 6px', fontSize:16, display:'flex', alignItems:'center', gap:7 }}>
            <AlertTriangle size={18}/> Client Re-price Request
          </h3>
          <p style={{ color:'var(--text-muted)', fontSize:12, margin:'0 0 16px' }}>
            Record the client’s reason for requesting a revised price. The tender will return to Bidding and the revision count will increment.
          </p>
          <Textarea label="Re-price Reason" required value={repriceReason} onChange={e => { setRepriceReason(e.target.value); setRepriceError(''); }}
            placeholder="e.g. Client advised our price is uncompetitive — requesting revised submission by 15/04…" rows={4} showTips={false} />
          {repriceError && <p style={{ color:'#f87171', fontSize:12, margin:'-8px 0 12px' }}>{repriceError}</p>}
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
            <button onClick={() => { setShowRepriceModal(false); setRepriceReason(''); setRepriceError(''); }}
              style={{ padding:'8px 16px', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, cursor:'pointer' }}>
              Cancel
            </button>
            <button onClick={handleReprice}
              style={{ padding:'8px 18px', background:'rgba(234,179,8,0.2)', border:'1px solid rgba(234,179,8,0.4)', borderRadius:8, color:'#fbbf24', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              Confirm Re-price
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Lost feedback modal ───────────────────────────────────────────────── */}
    {showLostModal && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <div style={{ background:'var(--card-bg)', borderRadius:14, width:'100%', maxWidth:440, padding:24, boxShadow:'0 24px 60px rgba(0,0,0,0.6)' }}>
          <h3 style={{ color:'var(--text)', margin:'0 0 6px', fontSize:16, display:'flex', alignItems:'center', gap:7 }}>
            <MessageSquare size={18} color="#eab308"/> Client / Prospect Feedback
          </h3>
          <p style={{ color:'var(--text-muted)', fontSize:12, margin:'0 0 16px' }}>
            Record why this tender was lost. This can be added now or later, but is mandatory for all lost tenders. It will appear in the commercial analysis.
          </p>
          <Textarea label="Feedback from client or prospect" value={lostText} onChange={e => setLostText(e.target.value)}
            placeholder="e.g. Client awarded to competitor on price. Feedback: our price was 15% higher than winning bid…" rows={4} showTips={false} />
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
            <button onClick={() => setShowLostModal(false)}
              style={{ padding:'8px 16px', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, cursor:'pointer' }}>
              Save for Later
            </button>
            <button onClick={handleSaveLostFeedback} disabled={!lostText.trim()}
              style={{ padding:'8px 18px', background: lostText.trim() ? 'rgba(234,179,8,0.2)' : 'rgba(255,255,255,0.05)', border:`1px solid ${lostText.trim() ? 'rgba(234,179,8,0.4)' : 'rgba(255,255,255,0.08)'}`, borderRadius:8, color: lostText.trim() ? '#eab308' : '#475569', fontSize:12, fontWeight:600, cursor: lostText.trim() ? 'pointer' : 'not-allowed' }}>
              Save Feedback
            </button>
          </div>
        </div>
      </div>
    )}
    {showContractReview && (
      <ContractReviewModal
        tenderId={tender.id}
        tenderName={tender.project_name}
        user={user}
        onClose={() => setShowContractReview(false)}
        onComplete={() => { setShowContractReview(false); onSave && onSave({ ...form }); }}
      />
    )}

    {/* ── Rejection Detail Modal ───────────────────────────────────────────── */}
    {showRejectionDetail && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
        <div style={{ background:'var(--card-bg)', borderRadius:14, width:'100%', maxWidth:500, padding:24, boxShadow:'0 24px 60px rgba(0,0,0,0.6)' }} onClick={e => e.stopPropagation()}>
          <h3 style={{ color:'var(--text)', margin:'0 0 16px', fontSize:16, fontWeight:600, display:'flex', alignItems:'center', gap:7 }}>
            <AlertTriangle size={18} color="#fb923c"/> Rejection Details
          </h3>
          <div style={{ background:'rgba(249,115,22,0.08)', border:'1px solid rgba(249,115,22,0.2)', borderRadius:10, padding:14, marginBottom:16 }}>
            <p style={{ color:'var(--text)', margin:0, fontSize:13, lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>
              {form.rejection_reason || '(No reason provided)'}
            </p>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'space-between', alignItems:'center', color:'var(--text-dim)', fontSize:12, marginBottom:16 }}>
            <span>Revision #{form.rejection_count}</span>
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button onClick={() => setShowRejectionDetail(false)}
              style={{ padding:'8px 16px', background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:12, fontWeight:500, cursor:'pointer' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    )}

    </>
  );
}

function normaliseStatus(s) {
  if (!s) return 'Bidding';
  const map = {
    won: 'Won', lost: 'Lost', dead: 'Dead', paused: 'Paused', bidding: 'Bidding',
    manager_review: 'Issued to Manager',
    submitted: 'Submitted-Awaiting',
    negotiation: 'Submitted-Awaiting',
    withdrawn: 'Dead',
  };
  return map[s.toLowerCase()] || s;
}

function getInitialStage(t) {
  if (!t) return 0;
  if (!t.deadline) return 0;  // No deadline — must complete Initial Entry first
  const s = normaliseStatus(t.status);
  if (t.decision_date || s === 'Won' || s === 'Lost') return 4;
  if (t.date_submitted) return 3;
  if (t.date_approved) return 3;
  if (s === 'Revision Required') return 1;
  if (t.date_to_manager || t.estimate_ready_at) return 2;
  if (t.price) return 1;
  return 0;
}
